import { useState, useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import { updateSEO, cleanupStructuredData } from '../../lib/seo';

export default function MediaPage() {
  const pressReleaseDate = "January 15, 2025";
  const [isPressKitOpen, setIsPressKitOpen] = useState(false);
  const [isMediaInquiryOpen, setIsMediaInquiryOpen] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    outlet: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ranchfireradio.com';
    
    updateSEO({
      title: 'Media & Press - RanchFire Radio Press Releases & Brand Assets',
      description: 'RanchFire Radio media center featuring press releases, brand assets, logos, and media contact information. Download press kit materials and submit media inquiries for Texas Country music coverage.',
      keywords: 'RanchFire Radio Press, Media Kit, Press Release, Brand Assets, Texas Country Media, Radio Press Kit',
      canonical: '/media',
      ogTitle: 'Media & Press - RanchFire Radio',
      ogDescription: 'Press releases, brand assets, and media contact information for RanchFire Radio',
      ogImage: 'https://public.readdy.ai/ai/img_res/71a1105f1fad2d2c5c8dc5d4f1603eb2.jpg',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Media & Press - RanchFire Radio",
        "description": "Press releases, news, and brand assets for RanchFire Radio",
        "url": `${siteUrl}/media`,
        "publisher": {
          "@type": "Organization",
          "name": "RanchFire Radio",
          "url": siteUrl,
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/1d2b59dfb9c0bc727c9fa79ffa1643de.png"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "info@ranchfireradio.com",
            "contactType": "Media Relations"
          }
        }
      }
    });

    return () => {
      cleanupStructuredData();
    };
  }, []);

  const pressKitAssets = [
    {
      category: 'Logos',
      items: [
        { name: 'Primary Logo (PNG)', size: '2.4 MB', icon: 'ri-image-line', url: 'https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/1d2b59dfb9c0bc727c9fa79ffa1643de.png' },
        { name: 'Logo - Light Background', size: '1.8 MB', icon: 'ri-image-line', url: 'https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/1d2b59dfb9c0bc727c9fa79ffa1643de.png' },
        { name: 'Logo - Dark Background', size: '1.8 MB', icon: 'ri-image-line', url: 'https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/1d2b59dfb9c0bc727c9fa79ffa1643de.png' },
      ]
    },
    {
      category: 'Brand Assets',
      items: [
        { name: 'Brand Guidelines (PDF)', size: '4.2 MB', icon: 'ri-file-pdf-line', url: '#' },
        { name: 'Color Palette', size: '156 KB', icon: 'ri-palette-line', url: '#' },
        { name: 'Typography Guide', size: '890 KB', icon: 'ri-font-size-2', url: '#' },
      ]
    },
    {
      category: 'Press Materials',
      items: [
        { name: 'Launch Press Release (PDF)', size: '245 KB', icon: 'ri-file-text-line', url: '#' },
        { name: 'Fact Sheet', size: '180 KB', icon: 'ri-file-list-line', url: '#' },
        { name: 'Founder Bio', size: '120 KB', icon: 'ri-user-line', url: '#' },
      ]
    },
    {
      category: 'Media Images',
      items: [
        { name: 'Hero Banner (High-Res)', size: '8.5 MB', icon: 'ri-image-2-line', url: 'https://readdy.ai/api/search-image?query=Rustic%20Texas%20country%20music%20radio%20station%20studio%20with%20vintage%20microphone%20warm%20amber%20lighting%20wooden%20acoustic%20panels%20cowboy%20hat%20on%20stand%20guitar%20leaning%20against%20wall%20southwestern%20desert%20landscape%20visible%20through%20window&width=1920&height=1080&seq=presskit1&orientation=landscape' },
        { name: 'Studio Photos', size: '12.3 MB', icon: 'ri-gallery-line', url: '#' },
        { name: 'Social Media Kit', size: '5.6 MB', icon: 'ri-share-line', url: '#' },
      ]
    }
  ];

  const handleDownload = (url: string, filename: string) => {
    if (url === '#') {
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleInquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitStatus('success');
    setInquiryForm({ name: '', email: '', outlet: '', message: '' });
    
    setTimeout(() => {
      setSubmitStatus('idle');
      setIsMediaInquiryOpen(false);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20rustic%20texture%20weathered%20wood%20grain%20pattern%20warm%20amber%20tones%20southwestern%20desert%20aesthetic%20vintage%20radio%20equipment%20silhouette&width=1920&height=600&seq=mediahero1&orientation=landscape')] bg-cover bg-center"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl text-white mb-6 uppercase tracking-wide" style={{ fontFamily: 'Anton, sans-serif' }}>
            Media
          </h1>
          <div className="w-24 h-1 bg-amber-700 mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Press Releases, News & Brand Assets
          </p>
        </div>
      </section>

      {/* Press Release Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Press Releases</h2>
            <p className="text-gray-600">Latest news and announcements from RanchFire Radio</p>
          </div>

          {/* Press Release Card */}
          <article className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
            <div className="p-8 sm:p-10">
              {/* Badge */}
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-700 text-white uppercase tracking-wider">
                  Press Release
                </span>
                <span className="text-sm text-gray-500">{pressReleaseDate}</span>
              </div>

              {/* Headline */}
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 leading-tight">
                RanchFire Radio Launches: A New Home for Alt Texas Country Music
              </h3>

              {/* Subheadline */}
              <p className="text-lg text-amber-800 font-semibold mb-6">
                RanchFireRadio.com brings Texas Country, Red Dirt, and Tex-Mex Country to listeners worldwide
              </p>

              {/* Body */}
              <div className="prose prose-lg text-gray-700 space-y-4">
                <p>
                  <strong>SAN ANTONIO, TX</strong> — RanchFire Radio officially launches today at <a href="https://ranchfireradio.com" className="text-amber-700 hover:text-amber-800 underline">RanchFireRadio.com</a>, delivering 24/7 streaming of authentic Country music rooted in the Southwest tradition.
                </p>

                <p>
                  The station features a curated mix of Texas Country, Red Dirt, Tex-Mex Country, and independent artists who carry forward the genuine spirit of Country music. RanchFire Radio is committed to playing music that's unfiltered, lived-in, and built to last.
                </p>

                <p>
                  "Country music didn't arrive here—it's always been here," says the RanchFire team. "We're not chasing trends. We're honoring a sound that's earned its loyalty through generations of real stories and real people."
                </p>

                <p>
                  Key features of RanchFire Radio include:
                </p>

                <ul className="list-disc pl-6 space-y-2">
                  <li>24/7 live streaming across all devices</li>
                  <li>Curated programming featuring emerging and established artists</li>
                  <li>Artist spotlight features and exclusive interviews</li>
                  <li>Community-driven content celebrating Southwest Country culture</li>
                  <li>Easy submission process for independent artists</li>
                </ul>

                <p>
                  Listeners can tune in now at <a href="https://ranchfireradio.com" className="text-amber-700 hover:text-amber-800 underline">RanchFireRadio.com</a> or through major streaming platforms including TuneIn, Spotify, and Apple Music.
                </p>

                <div className="pt-6 border-t border-amber-200 mt-8">
                  <p className="text-sm text-gray-600">
                    <strong>Media Contact:</strong><br />
                    RanchFire Radio<br />
                    <a href="mailto:info@ranchfireradio.com" className="text-amber-700 hover:text-amber-800">info@ranchfireradio.com</a>
                  </p>
                </div>

                <p className="text-center font-bold text-xl text-gray-900 pt-4">
                  Rooted. Real. Country.
                </p>
              </div>
            </div>
          </article>

          {/* Download/Share Options */}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => setIsPressKitOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line w-5 h-5 flex items-center justify-center"></i>
              Download Press Kit
            </button>
            <button 
              onClick={() => setIsMediaInquiryOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-900 text-gray-900 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
              Media Inquiries
            </button>
          </div>
        </div>
      </section>

      {/* Press Kit Modal */}
      {isPressKitOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsPressKitOpen(false)}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-[fadeIn_0.3s_ease-out]">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Press Kit</h3>
                <p className="text-gray-400 text-sm mt-1">Download brand assets and media materials</p>
              </div>
              <button 
                onClick={() => setIsPressKitOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {pressKitAssets.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-8 last:mb-0">
                  <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span className="w-8 h-0.5 bg-amber-700"></span>
                    {category.category}
                  </h4>
                  <div className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <div 
                        key={itemIndex}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-amber-50 transition-colors group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700 group-hover:bg-amber-200 transition-colors">
                            <i className={`${item.icon} text-lg`}></i>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-500">{item.size}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleDownload(item.url, item.name)}
                          className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-amber-700 hover:text-white text-gray-600 transition-all cursor-pointer"
                        >
                          <i className="ri-download-2-line text-lg"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                <i className="ri-information-line mr-1"></i>
                For additional assets, contact <a href="mailto:info@ranchfireradio.com" className="text-amber-700 hover:underline">info@ranchfireradio.com</a>
              </p>
              <button 
                onClick={() => setIsPressKitOpen(false)}
                className="px-5 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Inquiry Modal */}
      {isMediaInquiryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsMediaInquiryOpen(false)}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-[fadeIn_0.3s_ease-out]">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-700 to-amber-600 px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Media Inquiry</h3>
                <p className="text-amber-100 text-sm mt-1">Get in touch with our press team</p>
              </div>
              <button 
                onClick={() => setIsMediaInquiryOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {submitStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-4">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Inquiry Sent!</h4>
                  <p className="text-gray-600">We'll get back to you within 24-48 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleInquirySubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                    <input
                      type="text"
                      required
                      value={inquiryForm.name}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={inquiryForm.email}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-sm"
                      placeholder="john@publication.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Media Outlet / Publication</label>
                    <input
                      type="text"
                      value={inquiryForm.outlet}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, outlet: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-sm"
                      placeholder="Texas Monthly, Rolling Stone, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Message *</label>
                    <textarea
                      required
                      maxLength={500}
                      value={inquiryForm.message}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-sm resize-none"
                      placeholder="Tell us about your inquiry, interview request, or story angle..."
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1 text-right">{inquiryForm.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 text-white rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Send Inquiry
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
